import { ComponentType } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { ReverLoading } from '@itsrever/design-system'
import {
  isDashboardBlocked,
  useFeatureFlag
} from '@/application/use-feature-flag'
import { useAppSelector } from '@/redux/hooks'
import { Roles } from '@/utils'
import { useWarehousesScope } from '@/hooks/use-warehouses-scope'
import { useReturnsAndRefundsV2 } from '@/hooks/use-returns-and-refunds-v2'
import NotFound from '@/ui/pages/404.page'
import { NavigationTabs, PageHeader, StoreOnly } from '@/ui/shared'
import { BlockedDashboardOverlay } from '@/ui/components/overlays/blocked-dashboard-overlay'
import {
  ReturnsAndRefunds,
  ReturnsAndRefundsTab,
  TAB_SETTINGS
} from '@/ui/components/returns-and-refunds'
import ReturnConditionsV1 from './return-conditions.page'
import ReasonsV1 from './reasons.page'
import NonReturnableV1 from './non-returnable.page'
import { useRefundsTabs, useReturnConditionsTabs } from './configuration-tabs'

const TABS: Record<string, ReturnsAndRefundsTab> = {
  '/configuration/return-conditions': 'conditions',
  '/configuration/reasons': 'reasons',
  '/configuration/non-returnable': 'non-returnable',
  '/configuration/requirements': 'requirements',
  '/configuration/refunds': 'refunds'
}

// The per-store page a tab had before its setting moved to the group; tabs without one are new.
const V1_PAGES: Partial<Record<ReturnsAndRefundsTab, ComponentType>> = {
  conditions: ReturnConditionsV1,
  reasons: ReasonsV1,
  'non-returnable': NonReturnableV1
}

export default function ReturnsAndRefundsPage() {
  const { t } = useTranslation()
  const pathname = useLocation().pathname.replace(/\/$/, '')
  const tab = TABS[pathname]
  const profile = useAppSelector((s) => s.userApi.getMe)
  // getMe is dispatched from an effect: deciding before it lands would flash the 404 at an admin.
  const loadingProfile =
    profile.loading === 'idle' || profile.loading === 'pending'
  const isAdmin = profile.response.user?.role === Roles.ReadWrite
  const { scope, showGroup, showStore } = useWarehousesScope()
  const groupLevel = useReturnsAndRefundsV2()
  const returnTabs = useReturnConditionsTabs()
  const refundTabs = useRefundsTabs()
  const { isFlagEnabled: blocked } = useFeatureFlag(isDashboardBlocked, {
    pageName: 'configuration'
  })

  if (groupLevel.loading) return <ReverLoading />
  if (!groupLevel[TAB_SETTINGS[tab]]) {
    const V1Page = V1_PAGES[tab]
    return V1Page ? (
      <StoreOnly>
        <V1Page />
      </StoreOnly>
    ) : (
      <NotFound />
    )
  }
  if (loadingProfile) return <ReverLoading />
  if (!isAdmin) return <NotFound />
  const tabs = TAB_SETTINGS[tab] === 'refunds' ? refundTabs : returnTabs
  return (
    <>
      <NavigationTabs
        tabs={tabs}
        selectedIndex={tabs.findIndex((item) => item.link === pathname)}
      />
      {blocked && <BlockedDashboardOverlay />}
      {scope.kind === 'none' ? (
        <>
          <PageHeader title={t(`returns_refunds.tabs.${tab}`)} />
          <p className="px-7 py-6 text-sm text-neutral-600">
            {t('returns_refunds.no_scope')}
          </p>
        </>
      ) : (
        <ReturnsAndRefunds
          key={scope.group}
          scope={scope}
          tab={tab}
          onShowGroup={showGroup}
          onShowStore={showStore}
        />
      )}
    </>
  )
}
